import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { type PartnerCampaign } from '@/api/routes/partners/partners.campaigns.api';
import { type PartnerOffer } from '@/api/routes/partners/partners.offers.api';
import OfferStatusChip from '@/domain/offers/components/OfferStatusChip';
import {
  OFFERS_PAGE_SIZE,
  campaignSummaryLabel,
  formatOfferPeriod,
  offerStructureSummary,
} from '@/domain/offers/utils';
import { useDeviceMatch } from '@/hooks/useDeviceMatch';
import { useTranslate } from '@/state/i18n';

interface Props {
  offers: PartnerOffer[];
  campaignByOffer: Map<string, PartnerCampaign>;
  count: number;
  page: number;
  onPageChange: (page: number) => void;
  onOpen: (offer: PartnerOffer) => void;
}

/** P18 table on desktop, stacked rows on phones. "Campanha" comes from the Bodyprint, read-only. */
const OffersTable = ({ offers, campaignByOffer, count, page, onPageChange, onOpen }: Props) => {
  const t = useTranslate('domain.offers.components.OffersTable');
  const { isTablet } = useDeviceMatch();

  const campaignLabel = (offer: PartnerOffer) => {
    const campaign = campaignByOffer.get(offer.id);
    return campaign ? campaignSummaryLabel(campaign) : '—';
  };
  const period = (offer: PartnerOffer) =>
    formatOfferPeriod(offer.desiredStartsAt, offer.desiredEndsAt) ?? t('noPeriod');

  return (
    <>
      {isTablet ? (
        <Stack divider={<Divider />}>
          {offers.map((offer) => (
            <Stack key={offer.id} gap={0.75} sx={sx.mobileRow} onClick={() => onOpen(offer)}>
              <Typography variant="subtitle2">{offer.title}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[offerStructureSummary(offer), period(offer)].join(' · ')}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <OfferStatusChip status={offer.status} />
                {campaignByOffer.has(offer.id) ? (
                  <Typography variant="caption" color="text.secondary">
                    {campaignLabel(offer)}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('columns.offer')}</TableCell>
              <TableCell>{t('columns.action')}</TableCell>
              <TableCell>{t('columns.period')}</TableCell>
              <TableCell>{t('columns.status')}</TableCell>
              <TableCell>{t('columns.campaign')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offers.map((offer) => (
              <TableRow key={offer.id} hover sx={sx.row} onClick={() => onOpen(offer)}>
                <TableCell>
                  <Typography variant="subtitle2">{offer.title}</Typography>
                  {offer.status === 'changes_requested' || offer.status === 'rejected' ? (
                    <Typography variant="caption" color="text.secondary">
                      {t(`hint.${offer.status}`)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>{offerStructureSummary(offer)}</TableCell>
                <TableCell>{period(offer)}</TableCell>
                <TableCell>
                  <OfferStatusChip status={offer.status} />
                </TableCell>
                <TableCell>{campaignLabel(offer)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {count > OFFERS_PAGE_SIZE ? (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={OFFERS_PAGE_SIZE}
          rowsPerPageOptions={[]}
          onPageChange={(_, nextPage) => onPageChange(nextPage)}
          labelDisplayedRows={({ from, to, count: total }) =>
            t('pagination', { from, to, count: total })
          }
          getItemAriaLabel={(type) => t(`paginationAria.${type}`)}
        />
      ) : null}
    </>
  );
};

export default OffersTable;

const sx = {
  row: { cursor: 'pointer' },
  mobileRow: { p: 2, cursor: 'pointer' },
};
